import React from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { Plus, BarChart2, TrendingUp, DollarSign, PieChart, Smile, Calendar, CheckCircle, Target } from 'lucide-react';
import { MarketBrief } from '../../components/ui/market-brief';
import { GoalTracker } from '../../components/ui/goal-tracker';

const mockJournalEntries = [
  {
    symbol: 'EUR/USD',
    date: 'Today, 10:45 AM',
    text: 'Entered long position based on breakout of key resistance level. Maintained discipline and waited for confirmation.',
    pips: 38,
    comment: 'Good patience waiting for confirmation. Your entries on EUR/USD breakouts show 72% success rate.'
  },
  {
    symbol: 'GBP/JPY',
    date: 'Yesterday, 2:30 PM',
    text: 'Took short position after bearish engulfing pattern. Closed too early due to anxiety about economic announcement.',
    pips: 12,
    comment: 'Early exits have reduced your profits by ~35% over the last month. Consider using mechanical exit rules.'
  }
];

const HomePage = () => {
  return (
    <div className="min-h-screen bg-background text-foreground py-8 px-2 md:px-8">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column */}
        <div className="flex flex-col gap-6 lg:col-span-2">
          {/* New Journal Entry */}
          <Card className="p-6 flex flex-col gap-4">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-lg font-bold">New Trade Journal Entry</h2>
              <Button size="icon" variant="outline"><Plus /></Button>
            </div>
            <textarea
              className="w-full min-h-[100px] p-3 rounded-md bg-muted border border-border text-foreground"
              placeholder="Describe your trade here... (AI will help structure your thoughts)"
            />
            <div className="flex gap-2">
              <Button variant="default">AI Assist</Button>
              <Button variant="outline">Save Entry</Button>
            </div>
          </Card>

          {/* Recent Journal Entries */}
          <Card className="p-6">
            <h3 className="text-md font-semibold mb-4">Recent Journal Entries</h3>
            <div className="flex flex-col gap-4">
              {mockJournalEntries.map((entry, idx) => (
                <div key={idx} className="border-b border-border pb-4 last:border-b-0 last:pb-0">
                  <div className="flex items-center justify-between">
                    <span className="font-bold">{entry.symbol}</span>
                    <span className="text-xs text-muted-foreground">{entry.date}</span>
                  </div>
                  <div className="text-sm mt-1">{entry.text}</div>
                  <div className="text-green-500 text-xs mt-1">+{entry.pips} pips</div>
                  <div className="text-secondary text-xs mt-1 italic">"{entry.comment}"</div>
                </div>
              ))}
            </div>
          </Card>

          {/* Pattern Recognition, Vector Search, Learning Recommendations */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="p-4 flex flex-col gap-2">
              <div className="flex items-center gap-2 mb-2"><PieChart className="w-4 h-4" /><span className="font-semibold">Pattern Recognition</span></div>
              <div className="text-xs text-muted-foreground">"Based on vector analysis of your journal entries and trade performance, I've identified these patterns."</div>
              <div className="text-green-500 text-xs mt-1">+ Strength: Your morning EUR/USD trades show 78% win rate vs. 52% for afternoon entries.</div>
              <div className="text-red-500 text-xs">– Weakness: You tend to exit GBP pairs too early when in profit (avg 65% of potential).</div>
              <div className="text-secondary text-xs">Insight: Your best trades follow 2+ days of journaling consistency.</div>
            </Card>
            <Card className="p-4 flex flex-col gap-2">
              <div className="flex items-center gap-2 mb-2"><BarChart2 className="w-4 h-4" /><span className="font-semibold">Vector Search</span></div>
              <Input placeholder="Search past trades &amp; convos" className="mb-2" />
              <div className="text-xs text-muted-foreground">Try: "successful breakout trades" or "emotional trading patterns"</div>
            </Card>
            <Card className="p-4 flex flex-col gap-2">
              <div className="flex items-center gap-2 mb-2"><CheckCircle className="w-4 h-4" /><span className="font-semibold">Learning Recommendations</span></div>
              <ul className="list-disc pl-5 text-xs">
                <li>Exit strategy refinement for volatile pairs</li>
                <li>Managing trades during economic announcements</li>
                <li>Checklist: Confirming breakout validity</li>
              </ul>
            </Card>
          </div>
        </div>

        {/* Right Column */}
        <div className="flex flex-col gap-6">
          {/* Performance Snapshot */}
          <Card className="p-6 flex flex-col gap-4">
            <h3 className="font-semibold mb-2">Performance Snapshot</h3>
            <div className="grid grid-cols-2 gap-4 text-center">
              <div>
                <div className="text-green-500 text-xl font-bold">68%</div>
                <div className="text-xs text-muted-foreground">Win Rate</div>
              </div>
              <div>
                <div className="text-secondary text-xl font-bold">$1,245</div>
                <div className="text-xs text-muted-foreground">Monthly P&amp;L</div>
              </div>
              <div>
                <div className="text-primary text-xl font-bold">1.8</div>
                <div className="text-xs text-muted-foreground">Risk/Reward</div>
              </div>
              <div>
                <div className="text-xs font-bold">32</div>
                <div className="text-xs text-muted-foreground">Trades This Month</div>
              </div>
              <div>
                <div className="text-red-500 text-xl font-bold">5.2%</div>
                <div className="text-xs text-muted-foreground">Max Drawdown</div>
              </div>
              <div>
                <div className="text-green-500 text-xl font-bold">+12%</div>
                <div className="text-xs text-muted-foreground">Monthly Growth</div>
              </div>
            </div>
          </Card>

          {/* Emotional Analysis */}
          <Card className="p-6 flex flex-col gap-2">
            <h3 className="font-semibold mb-2">Emotional Analysis</h3>
            <div className="h-20 bg-muted rounded mb-2 flex items-end gap-1 px-2">
              {/* Placeholder for bar chart */}
              <div className="w-1/5 h-2/3 bg-secondary rounded"></div>
              <div className="w-1/5 h-1/2 bg-secondary/70 rounded"></div>
              <div className="w-1/5 h-4/5 bg-secondary rounded"></div>
              <div className="w-1/5 h-1/3 bg-secondary/50 rounded"></div>
              <div className="w-1/5 h-3/5 bg-secondary rounded"></div>
            </div>
            <div className="text-xs text-muted-foreground">"Your journal entries show increased confidence and reduced anxiety compared to last month. Keep building on this positive trend."</div>
          </Card>

          {/* Plan Adherence */}
          <Card className="p-6 flex flex-col gap-2">
            <h3 className="font-semibold mb-2">Plan Adherence</h3>
            <div className="flex items-center gap-2">
              <span className="text-green-500 font-bold text-lg">82%</span>
              <span className="text-xs text-muted-foreground">Plan Adherence Rate</span>
            </div>
            <div className="text-xs text-muted-foreground">"You've improved discipline in following your trading rules, especially for EUR/USD pairs."</div>
          </Card>

          {/* Daily Market Brief */}
          <MarketBrief />

          {/* Goal Tracker */}
          <GoalTracker />
        </div>
      </div>
    </div>
  );
};

export default HomePage; 